/**
 * TreeHierarchy — a top-down tree of cards, each level wired to the card that
 * opened it.
 *
 *                      ┌──────────────┐
 *                      │     root     │
 *                      └──────┬───────┘
 *          ┌──────────────┬───┴──────────┬──────────────┐
 *       [card]         [card]         [card]         [card]      ← level 0
 *                         │
 *                 ┌───────┴───────┐
 *              [card]          [card]                            ← level 1
 *
 * EACH LEVEL IS ONE HORIZONTAL LINE. It never wraps and it never scrolls: the
 * cards share the width they are given and shrink to fit. Both alternatives were
 * worse. A line that overflowed hid most of a wide level behind a scrollbar and
 * made the chart something to drag rather than something to read; a line that
 * wrapped grew the chart downwards and broke the wiring, because a bus cannot
 * hang a wire down to a card two lines below it.
 *
 * So the geometry is MEASURED, never computed. There is no card-width constant
 * kept in step with the stylesheet by hand — a card is whatever width its line
 * leaves it, and the bus is drawn between where the first and last cards
 * actually ended up.
 *
 * DEPTH IS DATA. The chart takes an array of levels and draws however many it is
 * given; it has no idea what they contain. Everything that varies — what a card
 * looks like, what opening one means, where the items come from — is the
 * caller's, and the chart owns only the geometry, the wiring and the one-open-
 * node-per-level rule.
 *
 * The caller holds the open state rather than the chart, because in practice it
 * drives data fetching too ("load this project's collections"), and a component
 * that hid it would force every caller to mirror it anyway.
 *
 * Levels appear one at a time, deliberately: a level renders only once the level
 * above it has something open. Branching everything at once gives a chart nobody
 * can read, and what is compared here is what sits inside one thing, not two of
 * them side by side.
 */
import type { ReactNode } from 'react';
import { useAnchors } from './use-anchors';
import './tree-hierarchy.css';

export interface TreeHierarchyLevel<T = unknown> {
  /** Stable id, used for React keys and test ids. */
  id: string;
  items: T[];
  keyOf: (item: T) => string;
  /** The card's contents. The chart supplies the card itself. */
  renderCard: (item: T, open: boolean) => ReactNode;
  /** Extra classes on the card — the caller's colours and states. */
  cardClassName?: (item: T, open: boolean) => string;
  /** Which card is open, and so which one the level below hangs from. Leave
   *  undefined on a leaf level: nothing opens beneath it. */
  openKey?: string | null;
  /** Expanding levels. Toggling off is the caller's business — it is the one
   *  that knows whether a second click should close or do nothing. */
  onToggle?: (key: string, item: T) => void;
  /** Leaf levels, where a card opens something that is not another row. */
  onSelect?: (item: T) => void;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  testId?: string;
  cardTestId?: (item: T) => string;
  /** Native tooltip for a card — for the detail a product-facing card should
   *  not shout but a reader may still want (an internal identifier, say). */
  cardTitle?: (item: T) => string;
  /** True when a card leads nowhere, so it should not invite the click. An
   *  empty branch that still opened put the reader in a row of empty cards to
   *  tell them what the card they came from had already said. */
  cardDisabled?: (item: T) => boolean;
}

export interface TreeHierarchyProps {
  /** The single node everything hangs from. Optional: a chart can start wide. */
  root?: ReactNode;
  /** Levels are heterogeneous by design: each one carries its own item type, so
   *  a single type parameter on the chart would force all of them to share one.
   *  Each level's own callbacks are typed where they are written. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  levels: TreeHierarchyLevel<any>[];
  className?: string;
  testId?: string;
}

/**
 * The connector from an opened card down into the line beneath it.
 *
 * Three segments, because the parent is wherever its own card happens to sit
 * while the line below is centred: down out of the parent, across to that line's
 * middle, and down into it. When the two already agree the crossing segment is
 * skipped and it reads as a single straight drop.
 */
function Joint({ parentX, childX }: { parentX: number; childX: number }) {
  const left = Math.min(parentX, childX);
  const width = Math.abs(parentX - childX);
  return (
    <div className="th-joint" aria-hidden="true">
      <span className="th-wire-v" style={{ left: parentX, top: 0, height: 10 }} />
      {width > 1 && <span className="th-wire-h" style={{ left, top: 8, width: width + 2 }} />}
      <span className="th-wire-v" style={{ left: childX, top: 8, height: 8 }} />
    </div>
  );
}

export function TreeHierarchy({ root, levels, className, testId }: TreeHierarchyProps) {
  const anchors = useAnchors(levels.map((l) => l.openKey ?? null));

  /* How deep to draw. A level is reached only once the one above it has a card
     open AND that card is still in its items — a stale key after a refetch would
     otherwise leave a line hanging off nothing. */
  let depth = levels.length ? 1 : 0;
  for (let i = 0; i < levels.length - 1; i += 1) {
    const key = levels[i].openKey;
    if (!key || !levels[i].items.some((item) => levels[i].keyOf(item) === key)) break;
    depth = i + 2;
  }

  return (
    <div className={`th-chart${className ? ` ${className}` : ''}`} data-testid={testId}>
      {root}
      {root && <div className="th-stem" />}

      {levels.slice(0, depth).map((level, i) => {
        const parent = anchors.metrics[i - 1];
        const self = anchors.metrics[i];
        // The bus runs from the first card's centre to the last card's, so it
        // stops at the cards rather than overhanging the ends of the line.
        const busWidth = self ? Math.max(0, self.lastCentre - self.firstCentre) : 0;

        return (
          <div key={level.id} className="th-level">
            {i > 0 && (
              <Joint parentX={parent?.cardCentre ?? 0} childX={self?.centre ?? 0} />
            )}

            <div className="th-line" ref={anchors.levelRef(i)} data-testid={level.testId}>
              {/* A single card needs no bus — the parent's connector lands on it. */}
              {level.items.length > 1 && busWidth > 0 && (
                <span
                  className="th-bus"
                  aria-hidden="true"
                  style={{ left: self.firstCentre, width: busWidth }}
                />
              )}

              {level.loading ? (
                <div className="th-inline-empty">{level.loadingMessage ?? 'Loading…'}</div>
              ) : level.items.length === 0 ? (
                <div className="th-inline-empty">{level.emptyMessage ?? 'Nothing here yet.'}</div>
              ) : (
                level.items.map((item) => {
                  const key = level.keyOf(item);
                  const open = level.openKey === key;
                  const extra = level.cardClassName?.(item, open) ?? '';
                  const dead = level.cardDisabled?.(item) ?? false;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-anchor={key}
                      disabled={dead}
                      className={`th-node${open ? ' is-open' : ''}${extra ? ` ${extra}` : ''}`}
                      // Only an expanding card is "pressed"; a leaf opens
                      // something elsewhere and is never in a chosen state.
                      aria-pressed={level.onToggle ? open : undefined}
                      onClick={() => {
                        level.onToggle?.(key, item);
                        level.onSelect?.(item);
                      }}
                      title={level.cardTitle?.(item)}
                      data-testid={level.cardTestId?.(item)}
                    >
                      {level.renderCard(item, open)}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TreeHierarchy;
